import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { IconButton } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { CSSProperties } from '@material-ui/core/styles/withStyles'
import Typography from '@material-ui/core/Typography'
import CheckCircleOutline from '@material-ui/icons/CheckCircleOutline'
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined'
import clsx from 'clsx'
import { onEscKeyPressOnDialog } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React from 'react'
export interface ConfirmationDialogStypeProps {
    /**
     * Whether to remove borders from top and bottom of footer and header respectively
     */
    removeHeaderBorder?: boolean
    removeFooterBorder?: boolean
    /**
     * To determine color of title in header
     */
    headerColor?: string
    variant?: 'default' | 'controlledWidth'
    dialogStyles?: CSSProperties
    contentStyles?: CSSProperties
    removeHeaderBorderForBulkAdd?: boolean
    /**
     * For setting overflow in dialog of large size
     */
    contentOverflow?: boolean
}
const useStyles = makeStyles((theme: Theme) => ({
    dialogTitle: {
        '& .MuiTypography-h6': {
            color: '#304959',
            fontSize: pxToRem(34),
        },
        '&.MuiDialogTitle-root': {
            padding: theme.spacing(3.125, 0, 2.5),
        },
        maxWidth: pxToRem(998),
    },
    dialogTitleBulkAdd: {
        borderBottom: ({
            removeHeaderBorderForBulkAdd,
        }: ConfirmationDialogStypeProps) =>
            !!removeHeaderBorderForBulkAdd ? 'none' : '1px solid #ACB1AD',
    },
    stringDialogTitle: {
        color: '#304959',
        fontSize: pxToRem(34),
        maxWidth: pxToRem(998),
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    },
    forAssociation: {
        paddingBottom: theme.spacing(2),
        paddingTop: theme.spacing(2),
        paddingLeft: theme.spacing(3),
        maxWidth: pxToRem(1050),
    },
    dialogCont: {
        '@media (max-width: sm)': {
            width: 250,
        },
        padding: theme.spacing(2.5, 0, 3.75),
        height: ({
            removeHeaderBorderForBulkAdd,
        }: ConfirmationDialogStypeProps) =>
            !removeHeaderBorderForBulkAdd ? 150 : 'auto',
    },

    addContentOverflow: {
        overflowY: 'auto',
    },
    dialogContent: ({
        contentStyles,
        contentOverflow,
        removeHeaderBorderForBulkAdd,
    }: ConfirmationDialogStypeProps) => {
        return {
            padding: theme.spacing(2.5, 0, 0, 0),
            overflow: contentOverflow ? 'auto' : 'hidden',
            color: theme.palette.common.black,
            fontSize: pxToRem(18),
            borderTop: removeHeaderBorderForBulkAdd
                ? 'none'
                : '1px solid #ACB1AD',
            borderBottom: '1px solid #ACB1AD',
            ...contentStyles,
        }
    },
    dialogContSmall: {
        width: pxToRem(500),
    },

    dialogContSmallForBulkAdd: {
        paddingBottom: 0,
        paddingTop: theme.spacing(2.5),
        overflow: 'hidden',
        color: theme.palette.common.black,
        fontSize: pxToRem(18),
        borderBottom: (props: ConfirmationDialogStypeProps) =>
            !!props.removeHeaderBorderForBulkAdd ? 'none' : '1px solid #ACB1AD',
    },

    dialogContDefault: {
        width: pxToRem(500),
        height: 'auto',
    },
    dialogContLarge: {
        paddingLeft: 0,
        '@media (max-width: sm)': {
            width: 500,
        },
        minWidth: 709,
        borderBottom: '1px solid #ACB1AD',
    },
    actionButton: {
        color: '#1899D4',
        fontSize: pxToRem(16),
        fontWeight: 500,
        marginBottom: 16,
        border: '1px solid #1899D4',
        borderRadius: pxToRem(50),
        background: 'transparent',
        boxShadow: 'none',
        '&:hover': {
            background: 'transparent',
            boxShadow: 'none',
        },
    },
    confirmationText: {
        paddingTop: theme.spacing(2),
        fontSize: pxToRem(18),
        color: theme.palette.common.black,
    },
    warningDialogCont: {
        '& .MuiDialog-paperScrollPaper': {
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
            maxWidth: (props: ConfirmationDialogStypeProps) =>
                props.variant === 'controlledWidth' ? pxToRem(785) : 'auto',
        },
    },
    customDialogStyle: ({ dialogStyles }: ConfirmationDialogStypeProps) => {
        return {
            '&.MuiDialog-root': {
                ...dialogStyles,
                zIndex: `${dialogStyles?.zIndex} !important`,
            },
        }
    },
    dialogAction: {
        '&.MuiDialogActions-root': {
            padding: theme.spacing(3.75, 0, 2.5),
        },
        '&.MuiDialogActions-spacing': {
            '& > *:not(first-child)': {
                marginLeft: theme.spacing(1.25),
            },
        },
    },
    checkBoxRound: {
        color: '#30b27e',
    },
}))

export interface ConfirmationDialogProps extends ConfirmationDialogStypeProps {
    /**
     * Defines state of confirmation dialog.
     */
    openDialog: boolean

    /**
     * For closing confirmation dialog
     */
    handleDialogClose?: VoidFunction

    /**
     * Trigger action on confirmation click
     */
    dialogActionCallback?: (_: React.MouseEvent<Element, MouseEvent>) => void
    /**
     * Trigger action on add only button click
     */
    dialogActionCallbackForImportOnly?: (
        _: React.MouseEvent<Element, MouseEvent>
    ) => void
    /**
     * Defines the heading of the confirmation dialog. Default: Confirm
     */
    dialogHeading?: string | React.ReactNode

    /**
     * Defines the content for the confirmation dialog.
     */
    contentNode?: React.ReactNode

    /*
     * Defines the confirmation message placed above action buttons on the confirmation dialog. Default: Are you sure you want to proceed?
     */
    confirmMessage?: string

    /*
     * Defines the text for cancel action button on confirmation dialog. Default: Cancel
     */
    cancelText?: string

    /*
     * Defines the text for confirmation action button  on confirmation dialog. Default: Ok
     */
    confirmText?: string

    /*
     * Disables the default confirm and cancel button when isConfirmButtonDisabled is true. Default: False
     */
    isConfirmButtonDisabled?: boolean

    /**
     * Defines the size for the confirmation dialog.
     */
    size?: 'small' | 'default' | 'large'
    /**
     * For setting overflow in dialog of large size
     */

    /*
     * Defines the text for confirmation action button  on confirmation dialog. Default: Ok
     */
    dialogType?: 'warning' | 'error' | 'success'

    calledForImportExecution?: boolean

    variant?: 'default' | 'controlledWidth'
    removeHeaderBorderForBulkAdd?: boolean

    /**
     * Custom action buttons to be added in the dialog
     */
    customDialogActions?: React.ReactNode
    /**
     * For showing import & publish button for Office Master User
     */
    showImportPublishButton?: boolean
    dialogActionCallbackForImportPublish?: (
        _: React.MouseEvent<Element, MouseEvent>
    ) => void
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
    openDialog,
    handleDialogClose,
    dialogActionCallback,
    dialogActionCallbackForImportOnly,
    dialogHeading = 'Confirm Remove',
    contentNode,
    confirmMessage = 'Are you sure you want to proceed?',
    cancelText = 'Cancel',
    confirmText = 'Remove',
    isConfirmButtonDisabled = false,
    size,
    dialogType,
    removeHeaderBorder,
    removeFooterBorder,
    headerColor = '#EB5757',
    contentOverflow,
    calledForImportExecution,
    variant,
    dialogStyles = {},
    contentStyles = {},
    removeHeaderBorderForBulkAdd,
    customDialogActions,
    showImportPublishButton,
    dialogActionCallbackForImportPublish,
}) => {
    const classes = useStyles({
        removeFooterBorder,
        removeHeaderBorder,
        headerColor,
        variant,
        dialogStyles,
        contentStyles,
        contentOverflow,
        removeHeaderBorderForBulkAdd,
    })

    return (
        <Dialog
            open={openDialog}
            data-testid="projectRemovalWarning"
            className={clsx(classes.warningDialogCont, {
                [classes.customDialogStyle]: !!Object.keys(dialogStyles).length,
            })}
            aria-labelledby="remove-project-properties"
            maxWidth="lg"
            onClose={onEscKeyPressOnDialog(handleDialogClose!)}
        >
            <DialogTitle
                className={clsx(
                    classes.dialogTitle,
                    removeHeaderBorderForBulkAdd && classes.dialogTitleBulkAdd
                )}
            >
                <GridContainer>
                    {dialogType === 'warning' && (
                        <GridItem>
                            <IconButton>
                                <ReportProblemOutlinedIcon
                                    fontSize="small"
                                    style={{ color: '#F0451F' }}
                                />
                            </IconButton>
                        </GridItem>
                    )}

                    {dialogType === 'success' && (
                        <GridItem>
                            <IconButton>
                                <CheckCircleOutline
                                    data-testid={'checkIcon'}
                                    className={clsx(classes.checkBoxRound)}
                                />
                            </IconButton>
                        </GridItem>
                    )}

                    <GridItem>
                        {typeof dialogHeading === 'string' ? (
                            <HoverTooltip text={dialogHeading}>
                                <Typography
                                    className={classes.stringDialogTitle}
                                    aria-label={dialogHeading}
                                >
                                    {dialogHeading}
                                </Typography>
                            </HoverTooltip>
                        ) : (
                            dialogHeading
                        )}
                    </GridItem>
                </GridContainer>
            </DialogTitle>
            <DialogContent
                className={clsx(
                    size === 'large'
                        ? classes.dialogContLarge
                        : classes.dialogCont,
                    size === 'small' && classes.dialogContSmall,
                    size === 'small' &&
                        removeHeaderBorderForBulkAdd &&
                        classes.dialogContSmallForBulkAdd,
                    size === 'default' && classes.dialogContDefault,
                    contentOverflow && classes.addContentOverflow,
                    classes.dialogContent
                )}
                data-testid="dialogContent"
            >
                {contentNode}
            </DialogContent>
            {confirmMessage?.length !== 0 && confirmMessage !== ' ' && (
                <Typography className={classes.confirmationText}>
                    {confirmMessage}
                </Typography>
            )}
            <DialogActions className={classes.dialogAction}>
                {!!cancelText && (
                    <Button
                        role="button"
                        type="button"
                        data-testid="cancelRemoveButton"
                        className={classes.actionButton}
                        onClick={handleDialogClose}
                        variant="contained"
                        aria-label={cancelText}
                        disabled={isConfirmButtonDisabled}
                    >
                        {cancelText}
                    </Button>
                )}

                {calledForImportExecution && (
                    <Button
                        data-testid="importOnlyButton"
                        onClick={dialogActionCallbackForImportOnly}
                        variant="contained"
                        className={classes.actionButton}
                    >
                        Import Only
                    </Button>
                )}
                {!!confirmText && !customDialogActions && (
                    <Button
                        data-testid="confirmRemoveButton"
                        onClick={dialogActionCallback}
                        variant="contained"
                        className={classes.actionButton}
                        disabled={isConfirmButtonDisabled}
                    >
                        {confirmText}
                    </Button>
                )}
                {customDialogActions}
                {showImportPublishButton && (
                    <Button
                        disabled={false}
                        data-testid="importPublishButton"
                        onClick={dialogActionCallbackForImportPublish}
                        variant="contained"
                        className={classes.actionButton}
                    >
                        Import + Publish
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default ConfirmationDialog
